/**
 * Damage Claims API Client
 * Epic 69: Garancia RMA & Cseregep
 */

import { ApiError, api } from './client';

// ============================================
// TYPES
// ============================================

export type DamageClaimType = 'GAR' | 'BH';
export type DamageClaimStatusType = 'PENDING' | 'APPROVED' | 'REJECTED' | 'SETTLED';

export interface DamageClaim {
  id: string;
  tenantId: string;
  rentalId: string;
  type: DamageClaimType;
  status: DamageClaimStatusType;
  repairCost: number;
  lostRevenue: number;
  totalClaim: number;
  note?: string;
  approvedBy?: string;
  approvedAt?: string;
  rejectionNote?: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface RentalReplacement {
  id: string;
  tenantId: string;
  originalRentalId: string;
  replacementEquipmentId: string;
  reason: DamageClaimType;
  note?: string;
  issuedBy: string;
  issuedAt: string;
}

export interface CreateDamageClaimInput {
  rentalId: string;
  type: DamageClaimType;
  repairCost: number;
  lostRevenue: number;
  note?: string;
}

export interface RejectClaimInput {
  rejectionNote: string;
}

export interface IssueReplacementInput {
  originalRentalId: string;
  replacementEquipmentId: string;
  reason: DamageClaimType;
  note?: string;
}

// ============================================
// API CALLS
// ============================================

export async function createDamageClaim(input: CreateDamageClaimInput) {
  return api.post<{ data: DamageClaim }>('/damage-claims', input);
}

export async function listDamageClaims(filters?: {
  status?: DamageClaimStatusType;
  rentalId?: string;
}) {
  const params = new URLSearchParams();
  if (filters?.status !== undefined) params.set('status', filters.status);
  if (filters?.rentalId !== undefined) params.set('rentalId', filters.rentalId);
  const query = params.toString();
  return api.get<{ data: DamageClaim[] }>(`/damage-claims${query ? `?${query}` : ''}`);
}

export async function getDamageClaim(id: string) {
  return api.get<{ data: DamageClaim }>(`/damage-claims/${id}`);
}

export async function approveDamageClaim(id: string) {
  return api.patch<{ data: DamageClaim; shouldGenerateInvoice: boolean }>(
    `/damage-claims/${id}/approve`,
    {}
  );
}

export async function rejectDamageClaim(id: string, input: RejectClaimInput) {
  return api.patch<{ data: DamageClaim }>(`/damage-claims/${id}/reject`, input);
}

export async function issueReplacement(claimId: string, input: IssueReplacementInput) {
  return api.post<{ data: RentalReplacement; contractNumber: string }>(
    `/damage-claims/${claimId}/replacement`,
    input
  );
}

export async function getReplacements(rentalId: string) {
  return api.get<{ data: RentalReplacement[] }>(`/damage-claims/${rentalId}/replacements`);
}

// ============================================
// Kártérítési igény — művelet-hiba osztályozás (68-kör 1️⃣ · 2/2)
// ============================================

/**
 * A kártérítési igény MUTÁCIÓS műveletei (jóváhagyás, elutasítás) hibájának
 * i18n-kulcsra képezése.
 *
 * ⚠️ (A)-alak — 🚦-ratify 2026-09-02: a `(1)` strukturált `error.code` ág itt is
 * SZÁNDÉKOSAN HIÁNYZIK. Mérve: a BE `service/controllers/damage-claim.controller.ts`
 * csak `BadRequestException('szöveg')` / `NotFoundException('szöveg')` alakot dob
 * ⇒ `ApiError.code === undefined`. Indoklás és bővítési út: lásd
 * `courierActionErrorToastKey` (courier-delivery.ts).
 */
export function damageClaimActionErrorToastKey(error: unknown): string {
  if (!(error instanceof ApiError)) return 'service.damageClaim.actionError.failed';

  // (2) HTTP-status-fallback
  switch (error.statusCode) {
    case 404:
      return 'service.damageClaim.actionError.notFound';
    case 409:
      return 'service.damageClaim.actionError.conflict';
    case 400:
    case 422:
      return 'service.damageClaim.actionError.validation';
    default:
      return 'service.damageClaim.actionError.failed';
  }
}
